/**
 * Thin fetch wrapper around the internal admin API.
 * HTTP Basic Auth is handled natively by the browser: we never touch credentials here.
 */

/** Absolute origin so a page URL carrying Basic Auth credentials (user:pass@host) never leaks into fetch(). */
const ORIGIN = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
export const API_BASE = `${ORIGIN}/bo-api`;

export class ApiError extends Error {
  readonly status: number;
  readonly details: unknown;

  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

export function buildUrl(path: string, query?: Query): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function toApiError(res: Response): Promise<ApiError> {
  let message = `${res.status} ${res.statusText}`;
  let details: unknown;
  try {
    const payload = (await res.json()) as { error?: string; details?: unknown };
    if (payload?.error) message = payload.error;
    details = payload?.details;
  } catch {
    // non-JSON error body: keep the status line
  }
  return new ApiError(res.status, message, details);
}

async function request<T>(method: string, path: string, options: { query?: Query; body?: unknown } = {}): Promise<T> {
  const res = await fetch(buildUrl(path, options.query), {
    method,
    headers: options.body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, { query }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body: body ?? {} }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body: body ?? {} }),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
